import { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { useMockAuth } from '../../hooks/useMockAuth';
import { useTheme } from '../../contexts/ThemeContext';

interface CreatorsLayoutProps {
  children: ReactNode;
}

/**
 * CreatorsLayout - Layout for the Creators section (creator tools)
 * Includes sidebar navigation for creator-specific features
 */
export function CreatorsLayout({ children }: CreatorsLayoutProps) {
  const { user } = useMockAuth();
  const { theme } = useTheme();

  const colors = {
    dark: {
      sidebarBg: '#1a1a2e',
      sidebarBorder: '#2d2d44',
      mainBg: '#0a0a0f',
      cardBg: 'rgba(26, 26, 46, 0.6)',
      cardBorder: 'rgba(102, 126, 234, 0.2)',
      text: '#ffffff',
      textMuted: '#9ca3af',
      linkColor: '#d1d5db',
      linkHover: '#2d2d44',
      activeLink: '#667eea'
    },
    light: {
      sidebarBg: '#f9fafb',
      sidebarBorder: '#e5e7eb',
      mainBg: '#ffffff',
      cardBg: '#ffffff',
      cardBorder: '#e5e7eb',
      text: '#111827',
      textMuted: '#6b7280',
      linkColor: '#4b5563',
      linkHover: '#f3f4f6',
      activeLink: '#2563eb'
    }
  };

  const c = colors[theme];

  return (
    <div style={{ display: 'flex', minHeight: '100%' }}>
      {/* Sidebar */}
      <aside style={{
        width: '16rem',
        backgroundColor: c.sidebarBg,
        borderRight: `1px solid ${c.sidebarBorder}`,
        padding: '1.5rem'
      }}>
        <div style={{ marginBottom: '2rem' }}>
          <h2 style={{
            fontSize: '1.25rem',
            fontWeight: 'bold',
            color: c.text,
            marginBottom: '0.5rem'
          }}>
            Creators Panel
          </h2>
          {user?.organizationId && (
            <p style={{
              fontSize: '0.875rem',
              color: c.textMuted,
              margin: 0
            }}>
              {user.organizationId}
            </p>
          )}
        </div>

        <nav style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <Link
            to="/creators"
            style={{
              padding: '0.75rem 1rem',
              borderRadius: '0.375rem',
              color: c.linkColor,
              textDecoration: 'none',
              fontSize: '0.875rem',
              fontWeight: '500',
              transition: 'all 0.2s'
            }}
            activeProps={{
              style: {
                backgroundColor: c.activeLink,
                color: 'white'
              }
            }}
          >
            📊 Dashboard
          </Link>

          <Link
            to="/creators/products"
            style={{
              padding: '0.75rem 1rem',
              borderRadius: '0.375rem',
              color: c.linkColor,
              textDecoration: 'none',
              fontSize: '0.875rem',
              fontWeight: '500',
              transition: 'all 0.2s'
            }}
            activeProps={{
              style: {
                backgroundColor: c.activeLink,
                color: 'white'
              }
            }}
          >
            🎸 My Products
          </Link>

          <Link
            to="/creators/orders"
            style={{
              padding: '0.75rem 1rem',
              borderRadius: '0.375rem',
              color: c.linkColor,
              textDecoration: 'none',
              fontSize: '0.875rem',
              fontWeight: '500',
              transition: 'all 0.2s'
            }}
            activeProps={{
              style: {
                backgroundColor: c.activeLink,
                color: 'white'
              }
            }}
          >
            📦 Orders
          </Link>

          <Link
            to="/creators/licenses"
            style={{
              padding: '0.75rem 1rem',
              borderRadius: '0.375rem',
              color: c.linkColor,
              textDecoration: 'none',
              fontSize: '0.875rem',
              fontWeight: '500',
              transition: 'all 0.2s'
            }}
            activeProps={{
              style: {
                backgroundColor: c.activeLink,
                color: 'white'
              }
            }}
          >
            🔑 License Management
          </Link>

          <Link
            to="/creators/analytics"
            style={{
              padding: '0.75rem 1rem',
              borderRadius: '0.375rem',
              color: c.linkColor,
              textDecoration: 'none',
              fontSize: '0.875rem',
              fontWeight: '500',
              transition: 'all 0.2s'
            }}
            activeProps={{
              style: {
                backgroundColor: c.activeLink,
                color: 'white'
              }
            }}
          >
            📈 Analytics
          </Link>

          <Link
            to="/creators/publish"
            style={{
              padding: '0.75rem 1rem',
              borderRadius: '0.375rem',
              color: c.linkColor,
              textDecoration: 'none',
              fontSize: '0.875rem',
              fontWeight: '500',
              transition: 'all 0.2s'
            }}
            activeProps={{
              style: {
                backgroundColor: c.activeLink,
                color: 'white'
              }
            }}
          >
            🚀 Publish & CI/CD
          </Link>
        </nav>

        {/* Quick Stats */}
        <div style={{
          marginTop: '2rem',
          padding: '1rem',
          backgroundColor: c.cardBg,
          borderRadius: '0.5rem',
          border: `1px solid ${c.cardBorder}`,
          backdropFilter: theme === 'dark' ? 'blur(10px)' : 'none'
        }}>
          <h3 style={{
            fontSize: '0.75rem',
            fontWeight: '600',
            color: c.textMuted,
            textTransform: 'uppercase',
            marginBottom: '0.75rem'
          }}>
            Quick Stats
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
              <span style={{ color: c.textMuted }}>Products</span>
              <span style={{ fontWeight: '600', color: c.text }}>0</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
              <span style={{ color: c.textMuted }}>Active Licenses</span>
              <span style={{ fontWeight: '600', color: c.text }}>0</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
              <span style={{ color: c.textMuted }}>Revenue</span>
              <span style={{ fontWeight: '600', color: '#059669' }}>$0.00</span>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <div style={{ flex: 1, backgroundColor: c.mainBg }}>
        {children}
      </div>
    </div>
  );
}
